import "./ProjectStageMappingControl.css";
import * as React from "react";
import { SourceType, SourceType_ } from "../../../store/ExternalEpmConnectStore";
import { ComboBox, css, Icon, IconButton, ISelectableOption, Label, TooltipDelay, TooltipHost } from "office-ui-fabric-react";
import { TransformationPanel } from "../TransformationPanel";
import { ExternalFieldInfo, IFieldMapping, MappingType } from "../../../store/integration/common";
import { upperFirstSort } from "../../utils/common";
import { Field } from "../../../entities/Metadata";
import { get } from "../../../fetch-interceptor";
import EntityWarning from "../../common/warnings/EntityWarning";
import { IWarning } from "../../../entities/common";

type Props = {
    sourceType: SourceType;
    stageMapping?: IFieldMapping;
    ppmxField: Field;
    externalFields: ExternalFieldInfo[];
    externalFieldTypes: string[];
    onGetExternalFieldOptions?: (field: ExternalFieldInfo) => Promise<string[] | number[] | null>
    onMappingUpdated?: (value?: IFieldMapping) => void;
}

const ProjectStageMappingControl = (props: Props) => {
    const [showTransformationPanel, setShowTransformationPanel] = React.useState(false);
    const [externalField, setExternalField] = React.useState<string | undefined>(props.stageMapping?.externalField);
    const [transform, setTransform] = React.useState<string | undefined>(props.stageMapping?.transform);

    React.useEffect(() => {
        setExternalField(props.stageMapping?.externalField);
        setTransform(props.stageMapping?.transform);
    }, [props.stageMapping]);

    const sourceName = SourceType_.getName(props.sourceType);
    const externalOptions: ISelectableOption[] = props.externalFields
        .filter(_ => props.externalFieldTypes.includes(_.type))
        .map((_) => ({ key: _.internalName, text: _.name }))
        .sort((a, b) => upperFirstSort(a.text, b.text));

    const buildStageMapping = (state?: Partial<IFieldMapping>): IFieldMapping | undefined => {
        if (!state && !externalField || !!state && !state.externalField) {
            return undefined;
        }

        return {
            externalField: state ? state.externalField! : externalField!,
            ppmxField: props.ppmxField.name,
            transform: (state ? state.transform : transform) ?? "",
            mappingType: MappingType.ToPpmx,
            isReadonly: true,
        };
    }

    function _onGetStageNamesOptions() {
        return get<string[] | number[] | null>(`api/Process/stages/names`, {});
    }

    return <div className="project-stage-mapping">
        <Label>
            Select {sourceName} field for Project Stage mapping
            <InfoTooltip sourceName={sourceName} />
            {!!externalField && <WarningTooltip sourceName={sourceName} />}
        </Label>
        <div className="mapping">
            <div className="row">
                <div className="align-center">
                    <ComboBox
                        options={externalOptions}
                        selectedKey={externalField}
                        allowFreeform={true}
                        placeholder="Select field"
                        className="mapping-expression"
                        onChange={(e, option) => {
                            const newExternalField = option?.key as string;
                            setExternalField(newExternalField);
                            setTransform("")
                            props.onMappingUpdated?.(buildStageMapping({ externalField: newExternalField, transform: "" }));
                        }}
                    />
                    <IconButton key="settings"
                        menuIconProps={{ iconName: 'Settings' }}
                        title="Show Transformation Rules"
                        className={css('menu')}
                        disabled={!externalField}
                        onClick={() => setShowTransformationPanel(true)}
                    />
                </div>
            </div>
            {showTransformationPanel && <TransformationPanel
                value={transform}
                ppmxField={props.ppmxField}
                mappingType={MappingType.ToPpmx}
                ppmxFieldValuesSuggesions={_onGetStageNamesOptions()}
                externalFieldValuesSuggesions={externalField ? props.onGetExternalFieldOptions?.(props.externalFields.find(_ => _.internalName === externalField)!) : undefined}
                onOk={(value) => {
                    setShowTransformationPanel(false);
                    setTransform(value);
                    props.onMappingUpdated?.(buildStageMapping({ externalField, transform: value }));
                }}
                onDismiss={() => setShowTransformationPanel(false)}
            />}
        </div>
    </div>;
}

export default ProjectStageMappingControl;

const InfoTooltip = (props: { sourceName: string }) => {
    return <TooltipHost
        delay={TooltipDelay.zero}
        tooltipProps={{
            onRenderContent: () => <>
                Select the {props.sourceName ?? ""} field that represents the current stage of your project. Set up transformation rules if necessary.
            </>
        }}>
        <Icon iconName="info" onClick={(e) => e.stopPropagation()} className="message-icon" />
    </TooltipHost>;
};

const WarningTooltip = (props: { sourceName: string }) =>
    <EntityWarning
        warnings={[{} as IWarning]}
        onRenderBody={() => <>
            The project stage will sync from {props.sourceName} if a matching stage name exists in the process.<br />
            If multiple matches exist, the first one in the process will be used.<br />
            If there are stages in the process that require approvals, these approvals will be skipped.<br />
            Moving backward will reset later stages to Not Started state; moving forward will mark skipped stages as Completed.<br />
            Sync will pause if the project enters a stage that belongs to the Canceled or Suspended phase.
        </>}
    />;
